import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import Role from "models/role"
import User, { IUserQueryParams } from "models/user"
import UserRole from "models/user-role"

export interface AdminUsersSliceState {
    users: User[]
    focusedUser: User | null
    assignedRoles: Role[]
    revokedRoles: Role[]
    savingRoles: boolean
    userCount: number
    query: IUserQueryParams
}

export const initialState: AdminUsersSliceState = {
    users: [],
    focusedUser: null,
    assignedRoles: [],
    revokedRoles: [],
    savingRoles: false,
    userCount: 0,
    // No paging: load every user, sorting happens client-side
    query: {
        page: 0,
        limit: Number.MAX_SAFE_INTEGER
    } as IUserQueryParams
}

const adminUsersSlice = createSlice({
    name: 'admin-users',
    initialState,
    reducers: {
        setUsers(state, { payload }: PayloadAction<User[]>) {
            state.users = payload
        },
        addUser(state, { payload }: PayloadAction<User>) {
            state.users.unshift(payload)
            state.userCount += 1
        },
        setUser(state, { payload }: PayloadAction<User>) {
            const index = state.users.findIndex(u => u.id === payload.id)
            if (index === -1) return

            state.users[index] = { ...state.users[index], ...payload }
        },
        setFoucusedUser(state, { payload }: PayloadAction<User | null>) {
            state.focusedUser = payload
        },
        clearRoleChanges(state) {
            state.assignedRoles = [];
            state.revokedRoles = [];
        },
        appendUserRole(state, { payload }: PayloadAction<{ roles: Role[], user: User }>) {
            const { user, roles } = payload

            // Find the user. 
            const index = state.users.findIndex(u => u.id === user.id)
            if (index === -1) return

            // Assign each role
            for (let i = 0; i < roles.length; i++) {
                const role = roles[i];
                const userRole: UserRole = {
                    user,
                    userId: user.id,
                    roleId: role.id,
                    role,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                }

                // Ensure userRoles is initialized
                if (!state.users[index].userRoles) {
                    state.users[index].userRoles = []
                }

                // Prevent duplicates
                const alreadyHasRole = state.users[index].userRoles!.some(
                    ur => ur.roleId === role.id
                )
                if (!alreadyHasRole) {
                    state.users[index].userRoles!.push(userRole)
                }
            }
        },
        removeUserRole(state, { payload }: PayloadAction<{ roles: Role[], user: User }>) {
            const { user, roles } = payload
            const index = state.users.findIndex(u => u.id === user.id)
            if (index === -1) return

            for (let i = 0; i < roles.length; i++) {
                const role = roles[i];
                if (state.users[index].userRoles) {
                    state.users[index].userRoles = state.users[index].userRoles!.filter(
                        ur => ur.roleId !== role.id
                    )
                }
            }
        },
        appendToAssignedRole(state, { payload }: PayloadAction<Role>) {
            // We will have to make sure to manage the assigned and revoke role

            // If the focused user already has the role ignore. 
            if (!state.focusedUser)
                return;

            // If the role is in revokedRoles remove it
            state.revokedRoles = state.revokedRoles.filter(r => r.id !== payload.id);

            const roles = state.focusedUser.userRoles;

            if (roles && roles.some(r => r.roleId === payload.id))
                return;

            state.assignedRoles.push(payload);

        },
        appendToRevokedRole(state, { payload }: PayloadAction<Role>) {
            // We will have to make sure to manage the assigned and revoked role

            // If the focused user already has the role ignore. 
            if (!state.focusedUser)
                return;

            // If the role is in assignedRoles remove it
            state.assignedRoles = state.assignedRoles.filter(r => r.id !== payload.id);

            // If the user has the role then add to removed roles
            const roles = state.focusedUser.userRoles;
            if (roles && !roles.some(r => r.roleId === payload.id))
                return;

            state.revokedRoles.push(payload)
        },
        setSavingRoles(state, { payload }: PayloadAction<boolean>) {
            state.savingRoles = payload
        },
        setUserCount(state, { payload }: PayloadAction<number>) {
            state.userCount = payload
        },
        setQuery(state, { payload }: PayloadAction<IUserQueryParams>) {
            state.query = payload
        }
    }
})

export default adminUsersSlice.reducer

export const {
    setUsers,
    addUser,
    setUser,
    setFoucusedUser,
    appendUserRole,
    removeUserRole,
    appendToAssignedRole,
    appendToRevokedRole,
    clearRoleChanges,
    setSavingRoles,
    setUserCount,
    setQuery
} = adminUsersSlice.actions
